import React, {
  useState,
} from "react"

import useTitle
  from "@/hooks/useTitle"

import {
  PlusIcon,
  MinusIcon,
  ClipboardDocumentIcon,
  ArrowRightCircleIcon,
} from "@heroicons/react/24/outline"

import PulseLoader
  from "react-spinners/PulseLoader"

import {
  useGetOrgIntentionBoardsQuery,
} from "@/features/orgIntentionBoards/orgIntentionBoardsApiSlice"

const EtOrgIgniterIntentionBoardList = ({
  onView,
}) => {

  // First Created - 08 Sep 2026
  // Author - Atul Pandey
  // Purpose - Org Igniter view of Trinity Intention Boards
  //           available to the logged in user's organization.

  useTitle(
    "Intention Boards"
  )

  /*
  |--------------------------------------------------------------------------
  | 01 - State
  |--------------------------------------------------------------------------
  */

  const [
    showDetails,
    setShowDetails,
  ] =
    useState({})

  /*
  |--------------------------------------------------------------------------
  | 02 - Get Intention Boards
  |--------------------------------------------------------------------------
  |
  | Backend organization scoping is authoritative.
  |
  */

  const {
    data:
      orgIntentionBoardsData,

    isLoading,

    isError,

    error,
  } =
    useGetOrgIntentionBoardsQuery()

  /*
  |--------------------------------------------------------------------------
  | 03 - Toggle Details
  |--------------------------------------------------------------------------
  */

  const handleToggleDetails =
    intentionBoardId => {

      setShowDetails(
        prev => ({

          ...prev,

          [intentionBoardId]:
            !prev[
              intentionBoardId
            ],

        })
      )

    }

  /*
  |--------------------------------------------------------------------------
  | 04 - Copy Public URL
  |--------------------------------------------------------------------------
  */

  const handleCopyPublicUrl =
    async publicSlug => {

      const publicUrl =
        `${window.location.origin}/trinity/intention/${publicSlug}`

      await navigator
        .clipboard
        .writeText(
          publicUrl
        )

    }

  /*
  |--------------------------------------------------------------------------
  | 05 - Format Deadline
  |--------------------------------------------------------------------------
  */

  const formatDeadline =
    dateValue => {

      if (
        !dateValue
      ) {

        return "Not Set"

      }

      return new Date(
        dateValue
      )
        .toLocaleString()

    }

  /*
  |--------------------------------------------------------------------------
  | 06 - Loading
  |--------------------------------------------------------------------------
  */

  if (
    isLoading
  ) {

    return (

      <PulseLoader
        color={"#ea580c"}
      />

    )

  }

  /*
  |--------------------------------------------------------------------------
  | 07 - Error
  |--------------------------------------------------------------------------
  */

  if (
    isError
  ) {

    return (

      <div className="text-sm text-orange-700">

        {
          error
            ?.data
            ?.message ||
          "Unable to load Intention Boards."
        }

      </div>

    )

  }

  /*
  |--------------------------------------------------------------------------
  | 08 - Normalize Intention Boards
  |--------------------------------------------------------------------------
  */

  const orgIntentionBoards =
    orgIntentionBoardsData
      ?.ids
      ?.map(
        id =>
          orgIntentionBoardsData
            ?.entities[id]
      ) ||
    []

  /*
  |--------------------------------------------------------------------------
  | 09 - Render
  |--------------------------------------------------------------------------
  */

  return (

    <div className="bg-gray-200 m-1 p-1 rounded font-poppins mx-auto">

      {/*
      |--------------------------------------------------------------------------
      | Header
      |--------------------------------------------------------------------------
      */}

      <div className="bg-white m-1 p-3 rounded">

        <p className="text-2xl font-bold text-gray-800">
          Intention Boards
        </p>

        <p className="text-xs text-gray-500 mt-1">
          View and support Intention Boards for your organization.
        </p>

      </div>

      {/*
      |--------------------------------------------------------------------------
      | Empty State
      |--------------------------------------------------------------------------
      */}

      {orgIntentionBoards
        ?.length ===
        0 && (

        <div className="bg-white rounded m-1 p-4">

          <p className="text-sm text-gray-600">
            No Intention Boards are currently available for your organization.
          </p>

        </div>

      )}

      {/*
      |--------------------------------------------------------------------------
      | Board List
      |--------------------------------------------------------------------------
      */}

      {orgIntentionBoards
        ?.map(
          (
            item,
            index
          ) => (

            <div
              key={
                item
                  ?._id
              }
              className="m-1 p-1 bg-white rounded"
            >

              <div className="m-1 p-1 bg-gray-100 rounded">

                <div className="flex justify-between items-center">

                  <div className="flex justify-start items-center mx-2">

                    {/*
                    |--------------------------------------------------------------------------
                    | Expand / Collapse
                    |--------------------------------------------------------------------------
                    */}

                    <div
                      onClick={
                        () =>
                          handleToggleDetails(
                            item._id
                          )
                      }
                      className="m-1 p-1 cursor-pointer rounded-full h-7 w-7 flex justify-center items-center hover:bg-white"
                    >

                      {showDetails[
                        item._id
                      ] ? (

                        <MinusIcon className="h-6 w-6 text-orange-600" />

                      ) : (

                        <PlusIcon className="h-4 w-4 text-orange-600" />

                      )}

                    </div>

                    {/*
                    |--------------------------------------------------------------------------
                    | Board Summary
                    |--------------------------------------------------------------------------
                    */}

                    <div className="m-1 p-1">

                      <div className="flex justify-start items-center flex-wrap">

                        <p className="font-bold">

                          {index + 1} - {
                            item
                              ?.boardName
                          }

                        </p>

                        <p className="text-[10px] bg-orange-100 text-orange-700 rounded-full mx-2 px-2 py-1">

                          {
                            item
                              ?.boardStatus ||
                            "draft"
                          }

                        </p>

                        <p
                          className={`text-[10px] rounded-full mx-1 px-2 py-1 ${
                            item
                              ?.isActive
                              ? "bg-lime-100 text-lime-700"
                              : "bg-gray-200 text-gray-600"
                          }`}
                        >

                          {
                            item
                              ?.isActive
                              ? "Active"
                              : "Inactive"
                          }

                        </p>

                      </div>

                      <p className="text-xs text-gray-600 mt-1">

                        {
                          item
                            ?.headline ||
                          "No headline added"
                        }

                      </p>

                    </div>

                  </div>

                  {/*
                  |--------------------------------------------------------------------------
                  | Actions
                  |--------------------------------------------------------------------------
                  */}

                  <div className="flex justify-start items-center mx-2">

                    {/*
                    |--------------------------------------------------------------------------
                    | Copy Public URL
                    |--------------------------------------------------------------------------
                    */}

                    <div className="rounded-full flex justify-center items-center h-7 w-7 hover:bg-white">

                      <ClipboardDocumentIcon
                        onClick={
                          () =>
                            handleCopyPublicUrl(
                              item
                                ?.publicSlug
                            )
                        }
                        className="h-4 w-4 text-orange-600 cursor-pointer"
                      />

                    </div>

                    {/*
                    |--------------------------------------------------------------------------
                    | Open Board
                    |--------------------------------------------------------------------------
                    */}

                    <div className="rounded-full flex justify-center items-center h-7 w-7 hover:bg-white">

                      <ArrowRightCircleIcon
                        onClick={
                          () =>
                            onView(
                              item
                                ?._id
                            )
                        }
                        className="h-5 w-5 text-orange-600 cursor-pointer"
                      />

                    </div>

                  </div>

                </div>

                {/*
                |--------------------------------------------------------------------------
                | Expanded Details
                |--------------------------------------------------------------------------
                */}

                {showDetails[
                  item._id
                ] && (

                  <div className="m-1 p-1 bg-gray-200 rounded">

                    <div className="m-1 p-2 rounded bg-white">

                      {/*
                      |--------------------------------------------------------------------------
                      | Public URL
                      |--------------------------------------------------------------------------
                      */}

                      <div className="m-1 p-1">

                        <p className="text-xs font-bold">
                          Public URL
                        </p>

                        <p className="text-[10px] text-orange-700 break-all">

                          {
                            window.location.origin
                          }/trinity/intention/{
                            item
                              ?.publicSlug
                          }

                        </p>

                      </div>

                      {/*
                      |--------------------------------------------------------------------------
                      | Organization Code
                      |--------------------------------------------------------------------------
                      */}

                      <div className="m-1 p-1">

                        <p className="text-xs font-bold">
                          Organization Code
                        </p>

                        <p className="text-[10px] text-gray-600">

                          {
                            item
                              ?.orgCode ||
                            "Not Set"
                          }

                        </p>

                      </div>

                      {/*
                      |--------------------------------------------------------------------------
                      | Response Deadline
                      |--------------------------------------------------------------------------
                      */}

                      <div className="m-1 p-1">

                        <p className="text-xs font-bold">
                          Response Deadline
                        </p>

                        <p className="text-[10px] text-gray-600">

                          {
                            formatDeadline(
                              item
                                ?.responseDeadline
                            )
                          }

                        </p>

                        <p className="text-[9px] text-gray-500">

                          {
                            item
                              ?.timezone ||
                            "America/Los_Angeles"
                          }

                        </p>

                      </div>

                      {/*
                      |--------------------------------------------------------------------------
                      | Response Policy
                      |--------------------------------------------------------------------------
                      */}

                      <div className="m-1 p-1">

                        <p className="text-xs font-bold">
                          Late Responses
                        </p>

                        <p className="text-[10px] text-gray-600">

                          {
                            item
                              ?.allowLateResponses
                              ? "Allowed"
                              : "Not Allowed"
                          }

                        </p>

                      </div>

                      {/*
                      |--------------------------------------------------------------------------
                      | Guidance
                      |--------------------------------------------------------------------------
                      */}

                      <div className="m-1 p-1">

                        <p className="text-[10px] text-gray-500">
                          Open this Intention Board to view questions, manage candidates, review submitted responses, and record your recommendation.
                        </p>

                      </div>

                    </div>

                  </div>

                )}

              </div>

            </div>

          )
        )}

    </div>

  )

}

export default EtOrgIgniterIntentionBoardList